import type { SlackMember } from '../../api/users';

/**
 * Suchen und Sortieren in der Slack-Mitgliederliste.
 *
 * Als reine Funktion neben der Ansicht, weil sich Komponenten hier nicht testen
 * lassen — es gibt kein jsdom im Web-Paket. Dieselbe Aufteilung wie bei
 * `admin/permissions/draft.ts`.
 *
 * Gefiltert wird im Browser und nicht über den Server: Die Liste kommt ohnehin
 * am Stück, und jeder Tastendruck ein Netzaufruf wäre bei einem Endpunkt mit
 * knappem Kontingent die schlechtere Wahl.
 */
export function filterSlackMembers(members: readonly SlackMember[], term: string): SlackMember[] {
  const needle = term.trim().toLowerCase();

  const matching = needle
    ? members.filter(
        (member) =>
          member.displayName.toLowerCase().includes(needle) ||
          (member.handle ?? '').toLowerCase().includes(needle) ||
          (member.email ?? '').toLowerCase().includes(needle),
      )
    : [...members];

  return matching.sort(compareMembers);
}

/**
 * Gäste nach hinten, sonst nach Namen.
 *
 * Ein Gast im Workspace ist selten die gesuchte Person — jemand aus einer
 * Agentur etwa, der in einem Projektkanal sitzt. Ihn zwischen die Belegschaft
 * zu mischen, erhöht die Wahrscheinlichkeit, dass eine Verknüpfung auf die
 * falsche Zeile zeigt, und die entscheidet später mit, wer freigeben darf.
 */
function compareMembers(a: SlackMember, b: SlackMember): number {
  if (a.isGuest !== b.isGuest) return a.isGuest ? 1 : -1;

  // `localeCompare` mit deutschem Gebietsschema: Sonst landet „Ätna" hinter
  // „Zeppelin", und in einer Namensliste fällt genau das auf.
  return a.displayName.localeCompare(b.displayName, 'de');
}

/**
 * Die Zeile, die ein Slack-Konto in der Auswahl beschreibt.
 *
 * Der Handle steht dabei nicht bloß der Vollständigkeit halber: Bei zwei
 * Personen gleichen Namens ist er das Einzige, woran sie sich unterscheiden
 * lassen.
 */
export function describeMember(member: SlackMember): string {
  const parts = [member.handle ? `@${member.handle}` : undefined, member.email].filter(Boolean);
  return parts.join(' · ');
}
